import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
  Zoom,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import colors from '@/styles/colors'
import { CBox, RBox } from '@/pages/__styled'
import { RoundedFilledButton, RoundedOutlineButton } from '@/components/StyledComponents'
import { OddlyouNoteType, PrivacyType, TopicType, TopicTypes } from '@/utils/constants'
import {
  useAddNoteTopic,
  useCreateTopic,
  useGetTopicDetails,
  useGetTopics,
  useUploadMedia,
} from '@/hooks/api/dashboard'
import { ModernCreateModal, RInputModal } from '@/components'
import { Topic } from '@/types'
import { getTopicNameFromTopic } from '@/utils/helper'
import DoneIcon from '@mui/icons-material/Done'
import { toast } from 'react-hot-toast'
import FullScreenLoadingView from './FullScreenLoadingView'
import { useLocalStorage } from '@/hooks/common'
import { emitAppEvent } from '@/events/eventBus'
import { AppEvents } from '@/events/eventTypes'

interface SaveScreenshotModalProps {
  file: File
  open: boolean
  onClose: () => void
}

const CustomBackdrop = styled('div')({
  backdropFilter: 'blur(8px)',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
})

const SaveScreenshotModal = ({ file, open, onClose }: SaveScreenshotModalProps) => {
  const theme = useTheme()
  const [ewUser] = useLocalStorage('ewUser', null)
  const createTopic = useCreateTopic(ewUser?.userID)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const getTopics = useGetTopics()
  const uploadPhoto = useUploadMedia()
  const createTopicNote = useAddNoteTopic()
  const getTopicDetails = useGetTopicDetails()
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedTopicID, setSelectedTopicID] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creatingNewAlbum, setCreatingNewAlbum] = useState(false)
  const [topics, setTopics] = useState<Topic[]>([])

  useEffect(() => {
    loadTopics()
  }, [])

  useEffect(() => {
    if (getTopics.data) {
      const allData: Topic[] = getTopics.data || []
      let filtered = allData.filter((e) => e.topicType == TopicType.ALBUM.toString())
      setTopics(filtered)
    }
  }, [getTopics.data])

  const selectedTopicTitle = useMemo(() => {
    if (selectedTopicID && topics.length > 0) {
      const selectedTopic = topics.find((item) => item.topicID == selectedTopicID)
      return selectedTopic ? getTopicNameFromTopic(selectedTopic) : ''
    }
    return ''
  }, [selectedTopicID, topics])

  const blobURL = useMemo(() => {
    if (file) {
      return URL.createObjectURL(file)
    }
    return null
  }, [file])

  const loadTopics = (pageNo: number = 1) => {
    getTopics.mutate({
      pageNo,
      interaction: Number(PrivacyType.PRIVATE),
      topicType: Number(TopicType.ALBUM),
    })
  }

  const showNoResultView = useMemo(() => {
    return getTopics.isSuccess && getTopics.data.length == 0 && currentPage === 1
  }, [getTopics.isSuccess, getTopics.data])

  const onClickSave = async () => {
    setLoading(true)
    const fileInfo = await uploadPhoto.mutateAsync(file)
    const egoList = [
      {
        sequenceID: '1',
        egoID: fileInfo.egoID,
        photoAlbumURL: fileInfo.egoAlbumURL,
        photoFullURL: fileInfo.egoImageURL,
      },
    ]

    const noteText = {
      text: `<p>ScreenShot</p>`,
      attachments: [],
    }

    try {
      await createTopicNote.mutateAsync({
        noteName: 'Screenshot',
        topicID: selectedTopicID!,
        noteText: JSON.stringify(noteText),
        egoItems: egoList,
        noteType: OddlyouNoteType.IMAGE.toString(),
      })
      setLoading(false)
      toast.success('Screenshot Saved.')

      const topic = topics.find((e) => e.topicID == selectedTopicID)
      if (topic) {
        let updatedTopic = {
          ...topic,
          firstNote: {
            ...topic.firstNote,
            egoList,
          },
        }
        emitAppEvent(AppEvents.TOPIC_UPDATED, { topic: updatedTopic })
      }

      onClose()
    } catch (e: any) {
      setLoading(false)
      toast.error(e.toString())
    }
  }

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false)
    setCreatingNewAlbum(false)
    // Reload albums list to get any newly created albums
    // loadTopics(1)
  }

  const renderTopicRowItem = (topic: Topic, isSelected: boolean) => (
    <RBox
      key={`${topic.topicID}`}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '8px',
        p: '9px 12px',
        gap: '10px',
        backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.08)' : '#ffffff', // ← white card on f8f9fa bg
        border: isSelected ? '1px solid rgba(99, 102, 241, 0.35)' : '1px solid rgba(0, 0, 0, 0.07)', // ← soft dark border, always visible
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.1)' : '#ffffff',
          borderColor: isSelected ? 'rgba(99, 102, 241, 0.45)' : 'rgba(0, 0, 0, 0.14)',
          boxShadow: isSelected ? 'none' : '0 1px 4px rgba(0, 0, 0, 0.06)', // ← subtle lift on hover
        },
        '&:active': {
          transform: 'scale(0.998)',
        },
      }}
      onClick={() => setSelectedTopicID(topic.topicID)}
    >
      {/* Leading dot */}
      <Box
        sx={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          flexShrink: 0,
          backgroundColor: isSelected ? '#6366f1' : '#cbd5e1', // ← slate-300, visible on white
          transition: 'background-color 0.15s ease',
        }}
      />

      {/* Label */}
      <Typography
        sx={{
          flex: 1,
          fontSize: '15px',
          fontWeight: isSelected ? 500 : 400,
          color: isSelected
            ? '#6366f1' // ← indigo for selected
            : '#374151', // ← gray-700, readable on white
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          letterSpacing: '-0.01em',
          lineHeight: '20px',
          transition: 'color 0.15s ease',
        }}
      >
        {getTopicNameFromTopic(topic)}
      </Typography>

      {/* Check */}
      {isSelected && <DoneIcon sx={{ color: '#6366f1', fontSize: 15, flexShrink: 0 }} />}
    </RBox>
  )
  const onConfirmCreateNewAlbum = async (text: string) => {
    setCreatingNewAlbum(true)

    const topicType = TopicType.ALBUM

    const result = await createTopic.mutateAsync({
      topicName: text.trim(),
      topicType: topicType.toString(),
      allowContribution: 0,
      topicXData: 'X Data Here',
      privacyType: 3,
      cliques: [],
      connections: [],
    })

    if (result.isError) {
      toast.error('Create failed. Please try again!')
    } else {
      toast.success(`${TopicTypes[TopicType.ALBUM].title} created successfully!`)
      handleCloseCreateDialog()
      loadTopics(1)
    }
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Zoom}
      onClose={onClose}
      aria-describedby='alert-dialog-slide-description'
      maxWidth='lg'
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          components: {
            Root: CustomBackdrop,
          },
        },
      }}
      PaperProps={{
        sx: isMobile
          ? {}
          : {
              height: '85%',
              width: '85%',
              maxWidth: '1400px',
              borderRadius: '16px',
              overflow: 'hidden',
            },
      }}
    >
      <Box
        display={'flex'}
        flexDirection={isMobile ? 'column' : 'row'}
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          backgroundColor: '#ffffff',
          gap: 0,
        }}
      >
        <CBox
          sx={{
            display: isMobile ? 'none' : 'flex',
            height: '100%',
            width: '280px',
            px: '20px',
            py: '24px',
            backgroundColor: '#f8f9fa',
            borderRight: '1px solid #e2e8f0',
          }}
        >
          {!showNoResultView && (
            <Typography
              sx={{
                ml: '4px',
                mb: '16px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1a1a1a',
              }}
            >
              {`Select ${TopicTypes[TopicType.ALBUM].title}`}
            </Typography>
          )}

          <Box
            flex={1}
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            {(getTopicDetails.isLoading || (getTopics.isLoading && currentPage === 1)) && (
              <Box mt={1} display='flex' justifyContent={'center'}>
                <CircularProgress />
              </Box>
            )}

            {showNoResultView ? (
              <Box p={5} display='flex' justifyContent={'center'}>
                {`No ${TopicTypes[TopicType.ALBUM].tabTitle}`}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {topics.map((topic) => (
                  <React.Fragment key={topic.topicID}>
                    {renderTopicRowItem(topic, topic.topicID === selectedTopicID)}
                  </React.Fragment>
                ))}
              </Box>
              // <>{topics.map((e, i) => renderTopicRowItem(e, e.topicID == selectedTopicID))}</>
            )}
          </Box>
          <Button
            variant='outlined'
            fullWidth
            onClick={() => setShowCreateDialog(true)}
            sx={{
              mt: '12px',
              py: '10px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                backgroundColor: '#f8fafc',
              },
            }}
          >
            {`+ Create New ${TopicTypes[TopicType.ALBUM].title}`}
          </Button>
        </CBox>
        <CBox sx={{ flex: 1 }}>
          <RBox
            sx={{
              display: isMobile ? 'flex' : 'none',
              px: '30px',
              pt: '18px',
              justifyContent: 'space-between',
            }}
          >
            {!showNoResultView && (
              <RBox>
                <Typography
                  sx={{ mr: '4px' }}
                >{`Select ${TopicTypes[TopicType.ALBUM].title}:`}</Typography>
                <Select
                  defaultValue='Select'
                  value={selectedTopicTitle || ''}
                  onChange={(event: SelectChangeEvent) => {
                    const value = event.target.value
                    setSelectedTopicID(value)
                  }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <>{`-Select-`}</>
                    }
                    return selected
                  }}
                  sx={{
                    height: 34,
                    borderRadius: 100,
                    color: '#525E9C',
                    border: '1px solid #E7EDF3',
                    minWidth: '100px',
                    maxWidth: '150px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {topics.map((item, index) => {
                    const isSelected = item.topicID == selectedTopicID
                    return (
                      <MenuItem
                        value={item.topicID}
                        key={`${index}`}
                        sx={{
                          display: 'flex',
                          flex: 1,
                          color: isSelected ? colors.meshdomBlue : colors.themeBlack,
                        }}
                      >
                        {getTopicNameFromTopic(item)}
                      </MenuItem>
                    )
                  })}
                </Select>
              </RBox>
            )}
            <RoundedOutlineButton
              sx={{ px: '12px', py: '4px' }}
              onClick={() => setShowCreateDialog(true)}
            >
              {`+ Create New ${TopicTypes[TopicType.ALBUM].title}`}
            </RoundedOutlineButton>
          </RBox>
          <Box
            display={'flex'}
            flex={1}
            borderRadius={'12px'}
            sx={{
              backgroundColor: '#000000',
              zIndex: 10,
              m: isMobile ? 2 : 4,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                backgroundImage: blobURL ? `url(${blobURL})` : '',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              px: isMobile ? 2 : 4,
              pb: isMobile ? 2 : 4,
            }}
          >
            <Button
              variant='outlined'
              onClick={onClose}
              sx={{
                px: 4,
                py: '10px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              {'Cancel'}
            </Button>
            <Button
              variant='contained'
              disabled={selectedTopicID == null}
              onClick={onClickSave}
              sx={{
                px: 4,
                py: '10px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#2d2d2d',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#9ca3af',
                },
              }}
            >
              {`Save to ${TopicTypes[TopicType.ALBUM].title}`}
            </Button>
          </Box>
        </CBox>
      </Box>
      {loading && <FullScreenLoadingView loadingText={'Saving Screenshot...'} />}

      <RInputModal
        open={showCreateDialog}
        maxLength={30}
        onClose={handleCloseCreateDialog}
        title={`Create New ${TopicTypes[TopicType.ALBUM].title}`}
        text={''}
        placeholder={`Enter ${TopicTypes[TopicType.ALBUM].title} Title`}
        description=''
        submitText='Save'
        onSubmit={onConfirmCreateNewAlbum}
        shouldCloseOnSubmit={false}
        showLoading={creatingNewAlbum}
      />

      {/* <ModernCreateModal open={showCreateDialog} onClose={handleCloseCreateDialog} /> */}
    </Dialog>
  )
}

export default SaveScreenshotModal
